import React, { useState } from "react";
import s from "./ButtonMAS.module.scss";
import TextMAS from "components/TextMAS/TextMAS";
import { CircularProgress } from "@mui/material";

/**
 *
 * @param {Object} props
 * @param {"md"|"medium"|"sm"|"small"|"table-bar-button"|"dialog-button"} props.size
 * @param {"primary"|"secondary"|"text"|"text-only"|"table-bordered-button"} props.variant
 *
 * @returns
 */

const ButtonMAS = ({
  label,
  startIcon,
  endIcon,
  isLoading,
  children,
  size,
  variant,
  disabled,
  onClick,
  className,
  smallWidth,
  nowrap,
  ...props
}) => {
  // If isLoading is controlled by parent, use it directly. Otherwise, manage loading state internally when onClick returns a promise.
  const [internalLoading, setInternalLoading] = useState(false);
  // Determine loading state: prefer controlled isLoading prop, fallback to internal loading state
  const effectiveLoading = isLoading !== undefined ? isLoading : internalLoading;
  // Determine if the button has text content for loading state decisions
  const hasText = !!label || typeof children === "string";
  // Show overlay loading spinner only when loading, has text, and no icons to avoid layout shift
  const useOverlayLoading = effectiveLoading && hasText && !startIcon && !endIcon;
  const sizeClass = [
    "md",
    "medium",
    "sm",
    "small",
    "table-bar-button",
    "dialog-button",
  ].includes(size && size.toLowerCase())
    ? `buttonMAS-size-${size}`
    : "buttonMAS-size-md";

  const styleClass = [
    "primary",
    "secondary",
    "text",
    "text-only",
    "bordered",
  ].includes(variant && variant.toLowerCase())
    ? `buttonMAS-variant-${variant}`
    : "buttonMAS-variant-primary";

  const classNames = [
    smallWidth && s["buttonMAS-small-width"],
    s["buttonMAS-base"],
    s[sizeClass],
    s[styleClass],
    useOverlayLoading && s["buttonMAS-overlay-loading-host"],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const getContent = () => {
    if (!hasText && !startIcon && !endIcon && effectiveLoading)
      return (
        <div className={s["buttonMAS-loading"]}>
          <CircularProgress size="1em" />
        </div>
      );

    if (hasText)
      return (
        <div
          className={[
            s["buttonMAS-text"],
            useOverlayLoading && s["buttonMAS-overlay-label"],
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <TextMAS fontWeight="medium" nowrap={nowrap}>
            {label || children}
          </TextMAS>
        </div>
      );

    if (typeof children !== "string") return children;
  };

  return (
    <button
      className={classNames}
      onClick={async (event) => {
        if (!onClick || disabled || effectiveLoading) return;

        const result = onClick(event);
        if (
          isLoading === undefined &&
          result &&
          typeof result.then === "function"
        ) {
          setInternalLoading(true);
          try {
            await result;
          } finally {
            setInternalLoading(false);
          }
        }
      }}
      disabled={disabled || effectiveLoading}
      data-loading={effectiveLoading}
      data-loading-overlay={useOverlayLoading}
      {...props}
    >
      {startIcon && effectiveLoading ? (
        <div className={s["buttonMAS-loading"]}>
          <CircularProgress size="1em" />
        </div>
      ) : (
        <div className={s["buttonMAS-icon"]}>{startIcon}</div>
      )}
      {getContent()}
      {endIcon && effectiveLoading ? (
        <div className={s["buttonMAS-loading"]}>
          <CircularProgress size="1em" />
        </div>
      ) : (
        <div className={s["buttonMAS-icon"]}>{endIcon}</div>
      )}
      {useOverlayLoading && (
        <div className={s["buttonMAS-overlay-spinner"]}>
          <CircularProgress size="1em" />
        </div>
      )}
    </button>
  );
};

export default ButtonMAS;
